import { motion } from "framer-motion";
import SectionAtmosphere from "@/components/SectionAtmosphere";
import atmosphereFeatures from "@/assets/atmosphere-features.jpg";
import { lqipFor } from "@/generated/atmospheric-lqip";
import { riseFade } from "@/lib/motion";

const pillars = [
  {
    number: "01",
    title: "Daily Energy Check-In",
    description:
      "A brief, adjustable self-report for the context you choose to record. A wearable is not required, and a missed check-in is information about burden, not failure.",
  },
  {
    number: "02",
    title: "Visual Energy Map",
    description:
      "Review what you logged across days and contexts. The map is retrospective: it does not predict tomorrow, diagnose a cause, or turn an association into a medical conclusion.",
  },
  {
    number: "03",
    title: "Pattern Discovery",
    description:
      "See possible tendencies in your own entries, stated with uncertainty and enough context to judge for yourself. A pattern may be incomplete and does not prove causation.",
  },
  {
    number: "04",
    title: "Wellness Summary",
    description:
      "Create a user-controlled summary that distinguishes self-reported information from any optional device context. You decide whether to keep or share it; it is not continuous clinician monitoring.",
  },
];

const Features = () => (
  <SectionAtmosphere
    id="features"
    ariaLabelledBy="features-heading"
    src={atmosphereFeatures}
    optimizedBase="/opt/atmosphere-features"
    lqip={lqipFor(atmosphereFeatures)}
    origin="top right"
    glow="right"
    tone="cream"
    className="py-28 sm:py-36"
  >
    <div className="container mx-auto px-6 sm:px-10 lg:px-16 max-w-6xl">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-10% 0px" }}
        variants={riseFade}
        className="text-center space-y-5 mb-20"
      >
        <div className="eyebrow justify-center mx-auto">What is inside</div>
        <h2 id="features-heading" className="font-display text-foreground text-display-xl mx-auto max-w-3xl">
          Four lower-burden tools. <span className="italic text-primary">You remain the decision-maker.</span>
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          Myelina is intended for wellness and informational use. It does not diagnose, recommend treatment, predict a relapse, or tell you what MS will do next.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
        {pillars.map((pillar, i) => (
          <motion.div
            key={pillar.number}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-10% 0px" }}
            variants={riseFade}
            transition={{ delay: i * 0.06 }}
            className="group relative bg-card/60 backdrop-blur-sm border border-border rounded-2xl p-10 hover:border-primary/30 hover:shadow-elevation transition-all duration-500 overflow-hidden"
          >
            <span
              aria-hidden="true"
              className="absolute -right-2 -top-4 font-display text-primary/[0.08] select-none pointer-events-none transition-transform duration-500 group-hover:scale-110 group-hover:text-primary/[0.12]"
              style={{ fontSize: "clamp(5rem, 12vw, 9rem)", lineHeight: 1 }}
            >
              {pillar.number}
            </span>
            <div className="relative space-y-4">
              <div className="text-[10px] tracking-[0.28em] uppercase text-primary/70">
                {pillar.number}
              </div>
              <h3 className="font-display text-display-md text-foreground">{pillar.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{pillar.description}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </SectionAtmosphere>
);

export default Features;
